import { memo, useCallback, useEffect, useState } from "react";
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import {
  useHandleTextFieldChange,
} from "services/forms/hooks";

import { CONTACT_PHONE_EDIT_FORM_SCHEMA } from "./validation";

import { ContactPhoneEditProps } from "./types";

import { CheckBox } from "ui/components/CheckBox";
import { TextArea } from "ui/components/TextField/TextArea";
import {
  createContactPhoneAPI,
  getContactPhoneAPI,
  getContactPhonesAPI,
  updateContactPhoneAPI,
} from "services/api/contacts/api";
import { ContactPhoneEditFormValues, ContactPhoneRow } from "services/api/contacts/types";
import { initialPagination } from "ui/components/Table/consts";
import { initialContactPhoneEditForm } from "services/api/contacts/consts";
import { useTranslate } from "helpers";
import MobilePhoneTextField from "ui/components/TextField/MobilePhoneTextField/MobilePhoneTextField";
import PhoneTextField from "ui/components/TextField/PhoneTextField/PhoneTextField";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import CardSelect from "ui/components/CardSelect/CardSelect";
import { CardSelectOption } from "ui/components/CardSelect/types";

const phoneTypeCardOptions: CardSelectOption[] = [
  { id: "M", icon: "/media/icons/phone.svg", title: "Mobitel", description: "Mobilni telefon" },
  { id: "F", icon: "/media/icons/phone.svg", title: "Fiksni", description: "Fiksni telefon" },
];

const ContactPhoneEditCard: React.FC<ContactPhoneEditProps> = (props) => {
  const { phoneId, contactId, show, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const { createDialog } = useDialog();

  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [existingPhones, setExistingPhones] = useState<ContactPhoneRow[]>([]);
  const [formValues, setFormValues] = useState<ContactPhoneEditFormValues>(
    initialContactPhoneEditForm
  );

  // Učitava se pri svakom otvaranju — kod novog telefona id je uvijek prazan, pa samo promjena
  // id-a ne bi osvježila popis postojećih telefona.
  useEffect(() => {
    if (!show) return;
    loadForm();
  }, [phoneId, show]);

  const loadForm = async () => {
    const phones = await fetchExistingPhones();
    fetchPhone(phones);
  };

  const fetchExistingPhones = async (): Promise<ContactPhoneRow[]> => {
    try {
      const result = await getContactPhonesAPI({ ...initialPagination, pageSize: 100 }, contactId);
      setExistingPhones(result.data);
      return result.data;
    } catch {
      setExistingPhones([]);
      return [];
    }
  };

  const fetchPhone = async (phones: ContactPhoneRow[]) => {
    if (!phoneId) {
      // Prvi telefon kontakta je odmah primarni.
      setFormValues({ ...initialContactPhoneEditForm, primary: phones.length === 0 });
      setErrors({});
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getContactPhoneAPI(contactId, phoneId);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CONTACT_PHONE_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    // Provjeri da li već postoji primarni telefon (isključi trenutni telefon ako uređujemo)
    if (formValues.primary) {
      const existingPrimaryPhone = existingPhones.find(
        (phone) => phone.primary && phone.id !== phoneId
      );

      if (existingPrimaryPhone) {
        setErrors((prev) => ({
          ...prev,
          primary: `${t("admin.contact.phone.validation.primaryexists")}: ${existingPrimaryPhone.phoneNumber}`,
        }));
        return;
      }
    }

    setIsLoading(true);

    try {
      let result;
      if (!!phoneId) {
        result = await updateContactPhoneAPI(contactId, phoneId, formValues);
      } else {
        result = await createContactPhoneAPI(contactId, formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleCheckBoxChange = useCallback(() => {
    setFormValues((old) => ({ ...old, primary: !old.primary }));
  }, []);

  const handleCloseModal = () => {
    createDialog({
      onConfirm: onClose
    })
  }

  const content = (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="flex gap-3 h-full">
            <form noValidate className="text-left flex-1 p-4">
              <div className="">
                <div className="mb-3">
                  <CardSelect
                    options={phoneTypeCardOptions}
                    value={formValues.phoneTypeId}
                    onChange={(id) => setFormValues(prev => ({ ...prev, phoneTypeId: id }))}
                    columns={2}
                    error={errors.phoneTypeId}
                  />
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    {formValues.phoneTypeId === "M" ? (
                      <MobilePhoneTextField
                        type="text"
                        name="phoneNumber"
                        label={t("admin.contact.phone.edit.mobilephonenumber")}
                        placeholder={t("admin.contact.phone.edit.mobilephonenumber.placeholder")}
                        onChange={handleTextFieldChange}
                        error={errors.phoneNumber}
                        value={formValues.phoneNumber}
                        required
                      />
                    ) : (
                      <PhoneTextField
                        type="text"
                        name="phoneNumber"
                        label={t("admin.contact.phone.edit.phonenumber")}
                        placeholder={t("admin.contact.phone.edit.phonenumber.placeholder")}
                        onChange={handleTextFieldChange}
                        error={errors.phoneNumber}
                        value={formValues.phoneNumber}
                        required
                      />
                    )}
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <TextArea
                      name="description"
                      label={t("admin.contact.phone.edit.description")}
                      placeholder={t("admin.contact.phone.edit.description.placeholder")}
                      onChange={handleTextFieldChange}
                      error={errors.description}
                      value={formValues.description}
                      required
                      endIcon=""
                    ></TextArea>
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <CheckBox
                      name="primary"
                      onChange={handleCheckBoxChange}
                      checked={Boolean(formValues.primary)}
                      label={t("admin.contact.phone.edit.primary")}
                      error={errors.primary}
                    ></CheckBox>
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                <Button
                  variant="outlined"
                  children={t("admin.close")}
                  onClick={handleCloseModal}
                />
                <Button
                  variant="primary"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );

  if (show !== undefined) {
    return (
      <Modal show={show} onHide={onClose} position="centered" size="small">
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={phoneId ? t("admin.contact.phone.edit.title") : t("admin.contact.phone.add.title")}
          onHide={onClose}
        />
        <ModalBody>
          {show && content}
        </ModalBody>
      </Modal>
    );
  }

  return content;
};
export default memo(ContactPhoneEditCard);

